import { forwardRef, type CSSProperties } from 'react';
import RDP from 'react-datepicker';
import dayjs, { type Dayjs } from 'dayjs';
import { Clock, X } from 'lucide-react';
import { cn } from './cn';
import { keepPopperOnScreen } from './popoverPortal';

/** Ant Design `TimePicker` replacement (Dayjs value in/out), on react-datepicker. */
const SIZE: Record<string, string> = { small: 'h-8 text-sm', middle: 'h-9 text-sm', large: 'h-11 text-[15px]' };

const TimeInput = forwardRef<HTMLButtonElement, any>(function TimeInput(
  { value, onClick, onClear, placeholder, size = 'middle', disabled, allowClear, minCh, id, 'aria-label': ariaLabel }, ref,
) {
  return (
    <button
      type="button" ref={ref} onClick={onClick} disabled={disabled} id={id} aria-label={ariaLabel}
      className={cn('inline-flex w-full min-w-[7rem] items-center gap-2 rounded-control border border-line bg-surface px-3 text-left text-content transition-colors hover:border-brand-300 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60', SIZE[size])}
    >
      <Clock className="size-4 shrink-0 text-subtle" />
      <span className={cn('flex-1 truncate', !value && 'text-subtle')} style={minCh ? { minWidth: `${minCh}ch` } : undefined}>{value || placeholder}</span>
      {allowClear && value && (
        <span role="button" tabIndex={-1} aria-label="Clear" onClick={(e) => { e.stopPropagation(); onClear?.(); }} className="grid size-5 place-items-center rounded-full text-subtle hover:bg-surface-2 hover:text-content">
          <X className="size-3.5" />
        </span>
      )}
    </button>
  );
});

export interface TimePickerProps {
  value?: Dayjs | null;
  onChange?: (d: Dayjs | null, timeString: string) => void;
  format?: string;
  use12Hours?: boolean;
  minuteStep?: number;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  style?: CSSProperties;
  size?: 'small' | 'middle' | 'large';
  /** Passed in by Form.Item / Field so the label's htmlFor points at this control. */
  id?: string;
  /** Names the control where there is no visible label to point at it. */
  'aria-label'?: string;
}

export function TimePicker({
  value, onChange, format = 'HH:mm', use12Hours, minuteStep = 1, placeholder = 'Select time',
  disabled, allowClear = true, className, style, size = 'middle', id, 'aria-label': ariaLabel,
}: TimePickerProps) {
  const twelve = use12Hours || /[Ah]/.test(format);
  const df = twelve ? 'h:mm aa' : 'HH:mm'; // react-datepicker display (date-fns tokens)
  const emitFmt = twelve ? 'hh:mm A' : 'HH:mm'; // dayjs tokens for the emitted string
  // Reserve the width the value (and placeholder) actually need, so the field never clips its own text.
  const minCh = Math.max(df.length, placeholder.length) + 1;
  return (
    <div className={cn('gb-timepicker', className)} style={style}>
      <RDP
        // An invalid dayjs becomes an Invalid Date that react-datepicker cannot format — it throws and blanks the page
        // (DEF-058). An unreadable value shows as empty instead.
        selected={value && value.isValid() ? value.toDate() : null}
        onChange={(d: Date | null) => onChange?.(d ? dayjs(d) : null, d ? dayjs(d).format(emitFmt) : '')}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={minuteStep}
        timeCaption="Time"
        dateFormat={df}
        disabled={disabled}
        placeholderText={placeholder}
        // react-datepicker clones `customInput` with its OWN input props, `id` among them, so an id set only on the
        // element below is overwritten with undefined and the Form.Item label points at nothing — the same fix
        // DatePicker already carries. Live: the regularization dialog's Check-in / Check-out time (E2E 15 Sep).
        id={id}
        popperClassName="z-[var(--z-popover)]"
        // Shift, not just flip: the time list must not run off a phone screen's edge.
        popperModifiers={[keepPopperOnScreen]}
        customInput={<TimeInput id={id} aria-label={ariaLabel} size={size} disabled={disabled} allowClear={allowClear} minCh={minCh} onClear={() => onChange?.(null, '')} />}
      />
    </div>
  );
}
